/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect } from 'react';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useInView } from 'react-intersection-observer';

import {
    ListingDetailsPageProps,
    ListingDetailsPageWrapperProps
} from './ListingDetailsPage.types';

import {
    AdContainer,
    AgentDetailsContainer,
    AgentDetailsHeading,
    AmenitiesContainer,
    FeaturesContainer,
    GalleryContainer,
    GalleryUserActionsContainer,
    MapContainer,
    NavContainer,
    Page,
    Primary,
    RecentSales,
    Secondary,
    SecondaryContent,
    SectionHeading,
    SummaryDetailsAndGalleryContainer,
    UserActionsContainer,
    VisuallyHiddenHeading
} from './ListingDetailsPage.styles';

import ListingHead from 'listing-details/components/ListingHead/ListingHead';
import ListingGallery from 'listing-details/components/ListingGallery/ListingGallery';
import ListingSummaryDetails from 'listing-details/components/ListingSummaryDetails/ListingSummaryDetails';
import ListingFeatures from 'listing-details/components/ListingFeatures/ListingFeatures';
import ListingStaticMap from 'listing-details/components/ListingStaticMap/ListingStaticMap';
import ListingValuation from 'listing-details/components/ListingValuation/ListingValuation';
import ListingNavigation from 'listing-details/components/ListingNavigation/ListingNavigation';
import ListingAmenities from 'listing-details/components/ListingAmenities/ListingAmenities';
import AgentDetails from 'listing-details/components/AgentDetails/AgentDetails';
import {
    SignInPrompt,
    UserActions,
    UserActionsGallery
} from 'listing-details/components/UserActions/UserActions';
import ListingMoreInformation from 'listing-details/components/ListingMoreInformation/ListingMoreInformation';
import ListingDisclaimer from 'listing-details/components/ListingDisclaimer/ListingDisclaimer';
import ListingToursAndFloorplans from 'listing-details/components/ListingToursAndFloorplans/ListingToursAndFloorplans';
import ListingBreadcrumbs from 'listing-details/components/ListingBreadcrumbs/ListingBreadcrumbs';
import RecentSalesLink from 'listing-details/components/shared/RecentSalesLink/RecentSalesLink';
import ModalManager from 'listing-details/components/ModalManager/ModalManager';
import useModalManager from 'listing-details/components/ModalManager/ModalManager.hooks';

import { MobileOS, NextPageContextApp } from 'listing-details/types';
import { ADS_MOBILE_BREAKPOINT } from 'listing-details/constants';
import {
    SavedListingProvider,
    useSavedListingDispatch
} from 'listing-details/context';
import { useListingPageTracking } from 'listing-details/hooks';
import { getSavedListings } from 'listing-details/api/user';

import { LISTING_QUERY } from './ListingDetailsPage.queries';
import { getMobileOS } from './ListingDetailsPage.helpers';
import { useAdUnitCode } from './ListingDetailsPage.hooks';
import {
    trackStaticMapClick,
    trackRefreshSimilarPropertiesSplitTest
} from './ListingDetailsPage.tracking';

import { identifyUser } from 'shared/helpers/segment/identifyUser';
import graphQLWrapper from 'shared/graphql/withApollo';

import Error from 'shared/components/Layout/Error';
import { AdvertProvider } from 'shared/components/Adverts/AdvertProviderDfp';
import { AdvertSlotDfp } from 'shared/components/Adverts/AdvertSlotDfp';
import { coerceDfpAdTargetingObject } from 'shared/components/Adverts/lib/coerceDfpAdTargetingObject';
import {
    getListingPageData,
    getListingSectionFromString,
    getPathForNavApi,
    handleCanonicalPathRedirect,
    hasMortgageCalculator
} from 'listing-details/helpers';
import { AD_UNIT_PAGE, AD_UNIT_ROOTS } from 'shared/components/Adverts/types';
import { getPageSectionForAdUnit } from 'shared/components/Adverts/lib';
import { PAGE_MAPPING } from 'shared/components/Adverts/constants';

import { useSelector } from 'react-redux';
import {
    withOptimizely,
    WithOptimizelyProps,
    selectForcedFeatures,
    getOptimizelyFeatureFlag
} from 'shared/optimizely';

const DynamicMortgageCalculator = dynamic(
    () =>
        import(
            'listing-details/components/MortgageCalculator/MortgageCalculator'
        ),
    { ssr: false }
);

const DynamicSimilarProperties = dynamic(
    () =>
        import(
            'listing-details/components/SimilarProperties/SimilarProperties'
        ),
    { ssr: false }
);

const ListingDetailsPageContent: React.FC<ListingDetailsPageProps> = withOptimizely(
    ({
        listingId,
        mobileOS,
        data,
        optimizely
    }: ListingDetailsPageProps & WithOptimizelyProps) => {
        const listing = data.listing;
        const listingSection = getListingSectionFromString(
            listing.section || 'for-sale'
        );
        const isSaleProperty = ['for-sale', 'new-homes'].includes(
            listingSection
        );
        const listingsLink = data.backToResultsLink;
        const breadcrumbs = data.breadcrumbs;
        const reportListingURL = `/${listingSection}/details/report-content/${listing.listingId}/`;
        const uuid = listing.listingId;

        useListingPageTracking({ data: data.listing, listingSection });

        const adUnitCode = useAdUnitCode(listingSection);

        const showMortgageCalculator = hasMortgageCalculator(
            data.listing.analyticsTaxonomy.priceQualifier
        );

        const [refCalculator, inViewCalculator] = useInView({
            triggerOnce: true
        });

        const [refSimiliar, inViewSimiliar] = useInView({
            triggerOnce: true
        });

        const [
            activeModal,
            isModalShowing,
            openModal,
            closeModal
        ] = useModalManager();

        const staticMapClick = () => {
            trackStaticMapClick();
            openModal('map', null);
        };

        const savedListingDispatch = useSavedListingDispatch();

        useEffect(() => {
            identifyUser(window);
        }, []);

        useEffect(() => {
            // Default state to unknown saved status prior to making the
            // API call to determine whether it's saved or not
            savedListingDispatch({
                type: 'RESET'
            });

            getSavedListings().then((data) => {
                savedListingDispatch({
                    type: 'INITIALISE',
                    payload: {
                        listingId,
                        isUser: Boolean(data.user.loggedIn),
                        isSaved: Boolean(
                            data.favourites.listings?.includes(
                                listingId.toString()
                            )
                        )
                    }
                });
            });
        }, [listingId]);

        const isRefreshSimilarPropertiesFeatureEnabled = getOptimizelyFeatureFlag(
            'lex-701-similar-listings-refresh',
            useSelector(selectForcedFeatures),
            optimizely
        );

        useEffect(() => {
            trackRefreshSimilarPropertiesSplitTest(
                isRefreshSimilarPropertiesFeatureEnabled
                    ? 'variant'
                    : 'original'
            );
        }, [listingId]);

        return (
            <>
                <ListingHead data={data} />
                <NavContainer>
                    <ListingNavigation data={listingsLink} />
                </NavContainer>
                <Page id="main-content" data-testid="listing-details-page">
                    <Primary>
                        <SummaryDetailsAndGalleryContainer>
                            <ListingSummaryDetails
                                data={listing}
                                openModal={openModal}
                            />
                            <GalleryContainer aria-labelledby="listing-gallery-heading">
                                <VisuallyHiddenHeading id="listing-gallery-heading">
                                    Property photos
                                </VisuallyHiddenHeading>
                                <ListingGallery data={listing} />
                                <GalleryUserActionsContainer data-testid="gallery-user-actions">
                                    <UserActionsGallery
                                        listingId={listingId}
                                        mobileOS={mobileOS}
                                        openModal={openModal}
                                        section={listingSection}
                                    />
                                </GalleryUserActionsContainer>
                            </GalleryContainer>
                        </SummaryDetailsAndGalleryContainer>
                        <MapContainer aria-labelledby="listing-map-heading">
                            <VisuallyHiddenHeading id="listing-map-heading">
                                Property location
                            </VisuallyHiddenHeading>
                            <ListingStaticMap
                                data={listing}
                                onMapClick={staticMapClick}
                            />
                        </MapContainer>
                        <AmenitiesContainer aria-labelledby="listing-amenities-heading">
                            <VisuallyHiddenHeading id="listing-amenities-heading">
                                Nearby amenities
                            </VisuallyHiddenHeading>
                            <ListingAmenities data={listing} />
                        </AmenitiesContainer>
                        <UserActionsContainer>
                            <UserActions
                                listingId={listingId}
                                isStacked={true}
                                mobileOS={mobileOS}
                                openModal={openModal}
                                section={listingSection}
                            />
                        </UserActionsContainer>
                        <FeaturesContainer
                            aria-labelledby="listing-features-heading"
                            data-testid="page_features_section"
                        >
                            <SectionHeading
                                id="listing-features-heading"
                                size="6"
                            >
                                Features and description
                            </SectionHeading>
                            <ListingFeatures data={listing} />
                        </FeaturesContainer>
                        <ListingToursAndFloorplans
                            data={listing}
                            openModal={openModal}
                        />
                        <ListingMoreInformation
                            data={listing}
                            listingSection={listingSection}
                            reportListingURL={reportListingURL}
                            openModal={openModal}
                        />
                        <SignInPrompt section={listingSection} />
                        <AdvertSlotDfp
                            position={1}
                            sizes={[
                                [300, 250],
                                [320, 50]
                            ]}
                            adUnitCode={adUnitCode}
                            showOnly={['<', ADS_MOBILE_BREAKPOINT - 1]}
                            collapseEmpty={true}
                        />
                        {showMortgageCalculator ? (
                            <div
                                ref={refCalculator}
                                data-testid="calculator-container"
                            >
                                {inViewCalculator ? (
                                    <DynamicMortgageCalculator
                                        data={listing}
                                        listingSection={listingSection}
                                    ></DynamicMortgageCalculator>
                                ) : null}
                            </div>
                        ) : null}
                        <AdContainer>
                            <AdvertSlotDfp
                                position={0}
                                sizes={['fluid']}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.TEXT_LINK,
                                    getPageSectionForAdUnit(PAGE_MAPPING),
                                    AD_UNIT_PAGE.LDP
                                ]}
                                collapseEmpty={true}
                            />
                        </AdContainer>

                        <Secondary>
                            <SecondaryContent>
                                <AgentDetailsContainer aria-labelledby="agent-details-heading">
                                    <AgentDetailsHeading
                                        id="agent-details-heading"
                                        size="6"
                                    >
                                        Listed by
                                    </AgentDetailsHeading>
                                    <AgentDetails
                                        listingId={listingId}
                                        data={listing}
                                        openModal={openModal}
                                        section={listingSection}
                                    />
                                </AgentDetailsContainer>
                                <AdvertSlotDfp
                                    adUnitCode={adUnitCode}
                                    position={0}
                                    sizes={[
                                        [300, 250],
                                        [300, 600]
                                    ]}
                                    showOnly={['>', ADS_MOBILE_BREAKPOINT]}
                                    collapseEmpty={true}
                                    uuid={uuid}
                                />
                            </SecondaryContent>
                        </Secondary>

                        <ListingValuation />
                    </Primary>
                    <div
                        ref={refSimiliar}
                        data-testid="similar-properties-trigger"
                    >
                        {inViewSimiliar ? (
                            <DynamicSimilarProperties
                                data={listing}
                                numItems={
                                    isRefreshSimilarPropertiesFeatureEnabled
                                        ? 12
                                        : 4
                                }
                            />
                        ) : null}
                    </div>

                    {isSaleProperty && Boolean(listing.marketStats) && (
                        <RecentSales>
                            <RecentSalesLink
                                areaName={listing.marketStats?.areaName || ''}
                                areaUri={listing.marketStats?.areaNameUri || ''}
                                trackingLabel="house prices footer link"
                            />
                        </RecentSales>
                    )}

                    <ListingDisclaimer data={listing} />
                    <ListingBreadcrumbs data={breadcrumbs} />
                </Page>

                <ModalManager
                    data={listing}
                    listingId={listingId}
                    listingSection={listingSection}
                    activeModal={activeModal}
                    isModalShowing={isModalShowing}
                    closeModal={closeModal}
                />
            </>
        );
    }
);

export const ListingDetailsPage: NextPage<ListingDetailsPageWrapperProps> = ({
    errorCode,
    listingId,
    mobileOS,
    data
}: ListingDetailsPageWrapperProps) => {
    if (errorCode || !data) {
        return <Error statusCode={errorCode || 500} />;
    }

    const advertProvider = {
        targeting: coerceDfpAdTargetingObject(data.listing.adTargeting)
    };

    return (
        <SavedListingProvider>
            <AdvertProvider value={advertProvider}>
                <ListingDetailsPageContent
                    listingId={listingId}
                    mobileOS={mobileOS}
                    data={data}
                ></ListingDetailsPageContent>
            </AdvertProvider>
        </SavedListingProvider>
    );
};

ListingDetailsPage.getInitialProps = async (ctx: NextPageContextApp) => {
    const { query, req, res, asPath } = ctx;
    const listingIdParam = query['listingId'] as string;
    const listingId = parseInt(listingIdParam, 10);
    const path = getPathForNavApi(asPath || '', listingId);

    const data = await getListingPageData(
        ctx,
        {
            query: LISTING_QUERY,
            variables: {
                listingId,
                searchIdentifier: query['search_identifier'],
                path
            }
        },
        path
    );

    // Redirect check is only performed on the server
    if (req && res && data.data) {
        const canonicalPath = data.data.listing.listingUris.detail || '';
        handleCanonicalPathRedirect(req, res, canonicalPath);
    }

    // Some UI customisations are used on LDP based on mobile type
    let mobileOS: MobileOS = null;
    if (req) {
        mobileOS = getMobileOS(req.headers['user-agent'] || '');
    } else {
        mobileOS = getMobileOS(navigator.userAgent);
    }

    return { ...data, mobileOS };
};

export default graphQLWrapper(ListingDetailsPage);
